import { Component, Input, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CookieService } from "ngx-cookie-service";
import { ContactsService } from "src/app/pages/contacts/services/contacts.service";
import {
  ContactOverview,
  OverviewDeal,
  OverviewLead,
  OverviewLink,
  OverviewProperty,
  OverviewPropertyCategory,
  OverviewPropertyRole,
  OverviewVisit,
} from "../../models/contact-overview.model";

export type ContactOverviewTab =
  | "summary"
  | "leads"
  | "deals"
  | "visits"
  | "properties"
  | "activity";

interface PropertyGroup {
  category: OverviewPropertyCategory;
  labelKey: string;
  badgeClass: string;
  items: OverviewProperty[];
}

export type PropertyFilter = "ALL" | OverviewPropertyCategory;

// Ordem fixa das quatro secções da tab Imóveis (decisão 2026-09-28).
const PROPERTY_GROUPS: Omit<PropertyGroup, "items">[] = [
  { category: "BOUGHT", labelKey: "t-contact-overview.t-bought", badgeClass: "badge-soft-success" },
  { category: "SOLD", labelKey: "t-contact-overview.t-sold", badgeClass: "badge-soft-success" },
  { category: "POTENTIAL_BUY", labelKey: "t-contact-overview.t-potential-buys", badgeClass: "badge-soft-primary" },
  { category: "POTENTIAL_SELL", labelKey: "t-contact-overview.t-potential-sells", badgeClass: "badge-soft-info" },
];

// Chip de origem em cada linha: porque é que o imóvel está na categoria.
const ORIGIN_LABEL: Record<OverviewPropertyRole, string> = {
  BOUGHT: "t-origin-sale-completed",
  SOLD: "t-origin-sale-completed",
  IN_PROCESS_BUY: "t-in-process",
  IN_PROCESS_SELL: "t-in-process",
  OWNER: "t-owner",
  BUYER_INTEREST: "t-interested",
  PROCESS: "t-type-process",
  MEDIATION: "t-mediation-short",
  OPEN_SALE_DEAL: "t-origin-open-sale-deal",
  OPEN_LISTING: "t-origin-open-listing",
};

/**
 * Visão 360º do contacto (substitui o antigo modal "Histórico"): Resumo,
 * Leads, Negócios, Imóveis e Atividade, com link directo para cada registo.
 * A tab Atividade só pede o histórico quando é aberta.
 */
@Component({
  selector: "app-contact-overview",
  templateUrl: "./contact-overview.component.html",
  styleUrls: ["./contact-overview.component.scss"],
})
export class ContactOverviewComponent implements OnInit {
  @Input() contactId!: number;
  @Input() initialTab: ContactOverviewTab = "summary";

  activeTab: ContactOverviewTab = "summary";
  overview: ContactOverview | null = null;
  loading = true;
  error = false;

  activity: any[] = [];
  activityLoaded = false;
  loadingActivity = false;

  // Pré-calculados: nunca alimentar *ngFor com métodos/slices no template.
  displayName = "";
  initials = "";
  propertyGroups: PropertyGroup[] = [];
  visiblePropertyGroups: PropertyGroup[] = [];
  propertyFilter: PropertyFilter = "ALL";
  readonly propertyFilters: { value: PropertyFilter; labelKey: string }[] = [
    { value: "ALL", labelKey: "t-all" },
    ...PROPERTY_GROUPS.map((g) => ({ value: g.category, labelKey: g.labelKey })),
  ];
  recentDeals: OverviewDeal[] = [];
  recentLeads: OverviewLead[] = [];

  constructor(
    private contactsService: ContactsService,
    private router: Router,
    private cookiesService: CookieService,
    public activeModal: NgbActiveModal,
  ) {}

  ngOnInit(): void {
    this.activeTab = this.initialTab || "summary";
    if (!this.contactId) {
      this.loading = false;
      this.error = true;
      return;
    }

    const lang = this.cookiesService.get("lang") || null;
    this.contactsService.getContactOverview(this.contactId, lang).subscribe({
      next: (data) => {
        this.overview = data;
        this.prepareDerived(data);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = true;
      },
    });

    if (this.activeTab === "activity") {
      this.loadActivity();
    }
  }

  onTabChange(tab: ContactOverviewTab): void {
    this.activeTab = tab;
    if (tab === "activity") {
      this.loadActivity();
    }
  }

  private loadActivity(): void {
    if (this.activityLoaded || this.loadingActivity) return;
    this.loadingActivity = true;
    this.contactsService.getContactHistory(this.contactId).subscribe({
      next: (data) => {
        this.activity = data ?? [];
        this.activityLoaded = true;
        this.loadingActivity = false;
      },
      error: () => {
        this.activity = [];
        this.activityLoaded = true;
        this.loadingActivity = false;
      },
    });
  }

  private prepareDerived(data: ContactOverview): void {
    const c = data.contact;
    this.displayName =
      c.fullName ||
      [c.firstName, c.lastName].filter(Boolean).join(" ") ||
      c.email ||
      `#${c.id}`;
    this.initials = this.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("");

    this.propertyGroups = PROPERTY_GROUPS.map((group) => ({
      ...group,
      items: data.properties.filter((p) => p.category === group.category),
    }));
    this.applyPropertyFilter();

    this.recentDeals = data.deals.slice(0, 3);
    this.recentLeads = data.leads.slice(0, 3);
  }

  /**
   * Abre o registo numa nova aba e mantém o modal aberto. Leads e imóveis
   * não têm rota de detalhe: vão por query param, que as listas `/leads`
   * (`leadId`) e `/properties` (`openPropertyId`) consomem ao arrancar e
   * que funciona mesmo para leads arquivadas ou negócios fechados — a
   * visibilidade é decidida pelo back-end.
   */
  open(link: OverviewLink | null | undefined): void {
    if (!link) return;
    const url = this.buildLinkUrl(link);
    if (!url) return;
    window.open(url, "_blank", "noopener");
  }

  buildLinkUrl(link: OverviewLink): string | null {
    let tree;
    switch (link.type) {
      case "sale":
        tree = this.router.createUrlTree(["/deals/sale", link.id]);
        break;
      case "listing":
        tree = this.router.createUrlTree(["/deals/listing", link.id]);
        break;
      case "process":
        tree = this.router.createUrlTree(["/deals/process", link.id]);
        break;
      case "lead":
        tree = this.router.createUrlTree(["/leads"], {
          queryParams: { leadId: link.id },
        });
        break;
      case "property":
        tree = this.router.createUrlTree(["/properties"], {
          queryParams: { openPropertyId: link.id },
        });
        break;
      default:
        return null;
    }
    // Respeita o <base href> da app.
    return new URL(this.router.serializeUrl(tree), document.baseURI).toString();
  }

  closeModal(): void {
    this.activeModal.dismiss("Cross click");
  }

  setPropertyFilter(filter: PropertyFilter): void {
    this.propertyFilter = filter;
    this.applyPropertyFilter();
  }

  private applyPropertyFilter(): void {
    this.visiblePropertyGroups = this.propertyGroups.filter(
      (group) =>
        group.items.length > 0 &&
        (this.propertyFilter === "ALL" || group.category === this.propertyFilter),
    );
  }

  propertyCount(filter: PropertyFilter): number {
    if (filter === "ALL") return this.overview?.properties.length ?? 0;
    return this.propertyGroups.find((g) => g.category === filter)?.items.length ?? 0;
  }

  readonly starPositions = [1, 2, 3, 4, 5];

  starIcon(visit: OverviewVisit, position: number): string {
    return position <= (Number(visit.agentRating) || 0)
      ? "ri-star-fill"
      : "ri-star-line";
  }

  visitStatusKey(visit: OverviewVisit): string {
    if (visit.status === "CANCELED") return "t-deals-visit-status-canceled";
    if (visit.status === "DONE") return "t-deals-visit-status-completed";
    return new Date(visit.startAt).getTime() < Date.now()
      ? "t-deals-visit-status-overdue"
      : "t-deals-visit-status-scheduled";
  }

  visitStatusBadge(visit: OverviewVisit): string {
    if (visit.status === "CANCELED") return "badge-soft-danger";
    if (visit.status === "DONE") return "badge-soft-success";
    return new Date(visit.startAt).getTime() < Date.now()
      ? "badge-soft-secondary"
      : "badge-soft-warning";
  }

  originKey(row: OverviewProperty): string {
    return `t-contact-overview.${ORIGIN_LABEL[row.role] ?? "t-in-process"}`;
  }

  dealTypeKey(deal: OverviewDeal): string {
    return `t-contact-overview.t-type-${deal.type}`;
  }

  dealTypeBadge(deal: OverviewDeal): string {
    switch (deal.type) {
      case "listing":
        return "badge-soft-success";
      case "process":
        return "badge-soft-info";
      default:
        return "badge-soft-primary";
    }
  }
}
